import React, { useMemo, useState } from 'react'
import moment from 'moment'
import CreatorFrame from '../../../../shared/CreatorFrame'
import { hapticTick } from '../../../../../_utils/haptics'
import { useScrollLock } from '../../../../../_utils/useScrollLock'
import { PlusIcon, SparkIcon } from '../../social/components/icons'
import { kindLabel, ChannelIcon } from '../../plans/components/PlanCard'
import { blankCampaign } from '../data/build'
import { PostThumb } from './Previews'
import '../../plans/Plans.css'
import './Studio.css'

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`
const range = p => (p.start ? (moment(p.start).isSame(p.end, 'day') ? moment(p.start).format('ddd D MMM') : `${moment(p.start).format('D MMM')} to ${moment(p.end).format('D MMM')}`) : '')
const daysOf = p => (p.start && p.end ? moment(p.end).diff(moment(p.start), 'days') + 1 : 1)
const OCCASION = { gift: 'Gift vouchers', season: 'Season', occasion: 'Date to know' }
const FILTERS = [
  { id: 'all', label: 'Everything' },
  { id: 'seasons', label: 'Seasons and dates' },
  { id: 'gifts', label: 'Gift vouchers' },
  { id: 'diary', label: 'Filling the diary' },
]
// Bailey's plan kinds that fill the diary
const DIARY = ['fill', 'quiet', 'day']
const MANY = { post: 'posts', story: 'Stories', email: 'emails', sms: 'texts' }
const what = (p) => {
  const on = (p.items || []).filter(i => i.on)
  return ['post', 'story', 'email', 'sms'].map(ch => ({ ch, n: on.filter(i => i.channel === ch).length })).filter(x => x.n)
}

// A season drawn in its own colours until its kit arrives: bands of the
// palette and a circle or two, never the same arrangement twice
const SeasonArt = ({ palette = [], seed = '' }) => {
  const p = palette.length ? palette : ['#EDE9E3', '#D8D2C8', '#1A1A1E', '#F6F3EE', '#B9B2A6']
  let h = 0
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) % 997
  const cx = 30 + (h % 40)
  const cy = 20 + ((h * 7) % 30)
  return (
    <svg className="cst-season-art" viewBox="0 0 160 100" preserveAspectRatio="xMidYMid slice" aria-hidden>
      <rect width="160" height="100" fill={p[0]} />
      <rect y="62" width="160" height="38" fill={p[3] || p[1]} />
      <circle cx={cx + 60} cy={cy + 10} r="34" fill={p[1]} />
      <circle cx={cx + 20} cy={cy + 40} r="18" fill={p[2]} />
      <circle cx={140 - (h % 30)} cy={78} r="9" fill={p[4] || p[2]} />
    </svg>
  )
}

// Where a new campaign starts: nothing, one of Bailey's ideas right now, or a
// season in the year ahead (each listed once). Win-back and birthdays are
// journeys' work, so their shortcuts go there.
const StartPage = ({ plans = [], templates = [], loading, k, onOpen, onNavigate, onClose }) => {
  const [filter, setFilter] = useState('all')
  useScrollLock(true)
  const ideas = plans.filter(p => (p.items && p.items.length) || p.action)
  const shown = new Set(ideas.map(p => p.key))
  const year = useMemo(() => templates.filter(t => !shown.has(t.key)), [templates, plans]) // eslint-disable-line react-hooks/exhaustive-deps
  const open = (plan) => { hapticTick(); onOpen(plan) }
  const keep = (p) => {
    if (filter === 'all') return true
    if (filter === 'diary') return DIARY.includes(p.kind)
    if (filter === 'gifts') return p.occasion === 'gift'
    return p.kind === 'moment' && p.occasion !== 'gift'
  }
  const nudges = [
    { id: 'gift', label: 'Sell gift vouchers', go: () => onNavigate('/marketing-campaigns', { compose: 'email', template: 'gift-card-push' }) },
    { id: 'winback', label: 'Win back quiet clients', go: () => onNavigate('/marketing-journeys', { openRecipe: 'win-back' }) },
    { id: 'birthday', label: 'Birthday treats', go: () => onNavigate('/marketing-journeys', { openRecipe: 'birthday-treat' }) },
    { id: 'quiet', label: 'Fill quiet days', go: () => { const q = plans.find(p => DIARY.includes(p.kind)); if (q) open(q); else onNavigate('/marketing-campaigns', { compose: 'email', template: 'flash-sale-pop' }) } },
  ]
  const ideaCards = ideas.filter(keep)
  const yearCards = year.filter(keep)

  return (
    <CreatorFrame name="New campaign" onClose={onClose}>
      <div className="cst-start">
        <div className="cst-start-inner">
          <h1 className="cst-start-title">New campaign</h1>
          <p className="cst-start-lead">Posts, Stories, emails and texts together, from a day to six weeks. Bailey fills each one with your own photos, reviews, team and free times.</p>

          <button type="button" className="cst-blank" onClick={() => open(blankCampaign())}>
            <span className="cst-blank-icon"><PlusIcon size={20} /></span>
            <span><b>Start from nothing</b><small>Pick the dates, then add posts, Stories, texts and emails.</small></span>
          </button>

          <div className="cst-nudges" role="group" aria-label="Need a nudge?">
            <span className="mkp-label">Need a nudge?</span>
            <div className="cst-nudge-row">
              {nudges.map(n => <button key={n.id} type="button" className="mkp-chip" onClick={() => { hapticTick(); n.go() }}>{n.label}</button>)}
            </div>
          </div>

          <div className="cst-filters" role="radiogroup" aria-label="Show">
            {FILTERS.map(f => <button key={f.id} type="button" role="radio" aria-checked={filter === f.id} className={`cst-filter${filter === f.id ? ' is-on' : ''}`} onClick={() => { hapticTick(); setFilter(f.id) }}>{f.label}</button>)}
          </div>

          {loading && <p className="cst-muted">Bailey is looking at your diary…</p>}
          {!loading && ideaCards.length > 0 && (
            <section className="cst-start-sec" aria-label="Bailey’s ideas right now">
              <h2 className="mkp-label"><SparkIcon size={11} /> Bailey’s ideas right now</h2>
              <div className="cst-ideas">
                {ideaCards.map((p) => {
                  const art = (p.items || []).filter(i => i.on && i.post).slice(0, 2)
                  return (
                    <button key={p.key} type="button" className="cst-idea" onClick={() => (p.action ? onNavigate(p.action.path, p.action.state) : open(p))}>
                      <span className="cst-idea-art" aria-hidden>
                        {p.palette ? <SeasonArt palette={p.palette} seed={p.key} /> : null}
                        {art.map(i => <span key={i.id} className={`cst-idea-post is-${i.post.format === 'story' ? 'story' : 'post'}`}><PostThumb post={i.post} kit={k.kit} width={i.post.format === 'story' ? 52 : 72} /></span>)}
                      </span>
                      <span className="cst-idea-text">
                        <span className="mkp-kind">{kindLabel(p)}</span>
                        <b>{p.title}</b>
                        <small>{p.action ? p.why : `${range(p)} · ${plural(daysOf(p), 'day', 'days')}`}</small>
                        {!p.action && (
                          <span className="cst-idea-what">
                            {what(p).map(x => <span key={x.ch}><ChannelIcon channel={x.ch} size={11} />{x.n} {x.n === 1 ? (x.ch === 'story' ? 'Story' : x.ch === 'sms' ? 'text' : x.ch) : MANY[x.ch]}</span>)}
                          </span>
                        )}
                      </span>
                    </button>
                  )
                })}
              </div>
            </section>
          )}

          {filter !== 'diary' && yearCards.length > 0 && (
            <section className="cst-start-sec" aria-label="The year ahead">
              <h2 className="mkp-label">The year ahead</h2>
              <div className="cst-seasons">
                {yearCards.map(t => (
                  <button key={t.key} type="button" className="cst-season" onClick={() => open(t)}>
                    <SeasonArt palette={t.palette} seed={t.key} />
                    <span className="cst-season-text">
                      <b>{t.title}</b>
                      <small>{range(t)} · {plural(daysOf(t), 'day', 'days')}</small>
                      <span className="cst-season-kind">{OCCASION[t.occasion] || 'Season'}</span>
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}
          {!loading && filter === 'diary' && !ideaCards.length && (
            <p className="cst-muted">Your diary looks healthy for the next few weeks, so Bailey has nothing to fill right now.</p>
          )}
        </div>
      </div>
    </CreatorFrame>
  )
}

export default StartPage
